import { letterSheet, lettersModel } from "@aybuben/client/screens/letters.ts";
import { type LetterView } from "@aybuben/core/content/course.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { useEffect } from "react";

import { useCourse } from "../content/context.tsx";
import { useStrings } from "../i18n/index.tsx";
import { COL, FOCUS } from "../theme.ts";
import { Chip, CloseBtn, G, Sheet } from "./primitives.tsx";
import { useSound, SoundButton } from "./sound.tsx";

interface LettersViewProps {
	st: GameState;
	onPick: (L: LetterView) => void;
}

export function LettersView({ st, onPick }: LettersViewProps) {
	const course = useCourse();
	const t = useStrings();
	const { met, total, tiles } = lettersModel(st, course.views.letters());
	return (
		<div className="px-4 pt-5 pb-32">
			<div className="flex items-baseline justify-between gap-2">
				<h2 className="text-2xl font-extrabold">{t("letters.title")}</h2>
				{/* Letters met only ever goes up, which is why it is the one count shown. */}
				<span className="text-sm font-semibold" style={{ color: COL.mute }}>
					{t("letters.metCount", { met, total })}
				</span>
			</div>
			<p className="mt-1 text-sm" style={{ color: COL.mute }}>
				{t("letters.subtitle")}
			</p>
			<div className="mt-4 grid grid-cols-5 gap-2">
				{tiles.map(({ letter: L, seen }) => {
					return (
						<button
							key={L.id}
							onClick={() => onPick(L)}
							className={`flex flex-col items-center rounded-2xl py-2.5 transition active:translate-y-0.5 ${FOCUS}`}
							style={{
								background: COL.card,
								border: `2px solid ${COL.line}`,
								boxShadow: `0 3px 0 ${COL.line}`,
								opacity: seen ? 1 : 0.5,
							}}
						>
							<span className="flex items-baseline gap-1">
								{L.u && (
									<G size={18} style={{ color: COL.faint }}>
										{L.u}
									</G>
								)}
								<G size={26}>{L.l}</G>
							</span>
							<span className="mt-1 text-xs font-semibold" style={{ color: COL.mute }}>
								{L.r}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
}

interface LetterSheetProps {
	L: LetterView;
	onClose: () => void;
}

export function LetterSheet({ L, onClose }: LetterSheetProps) {
	const course = useCourse();
	const t = useStrings();
	const sound = useSound();
	const { unit: u, words, audio } = letterSheet(course, L);
	useEffect(() => {
		if (audio) sound.playAuto(audio);
	}, [audio, sound]);
	return (
		<Sheet onClose={onClose}>
			<div className="flex justify-end">
				<CloseBtn onClick={onClose} />
			</div>
			<div className="flex items-end justify-center gap-5">
				<G size={104}>{L.l}</G>
				{L.u && (
					<G size={60} style={{ color: COL.faint }}>
						{L.u}
					</G>
				)}
			</div>
			<div className="mt-3 text-center">
				<Chip>{L.r}</Chip>
			</div>
			<p className="mt-3 text-center" style={{ color: COL.mute }}>
				{L.h}
			</p>
			{words.length > 0 && (
				<div className="mt-5 flex flex-col gap-2">
					{words.map((w) => (
						<div
							key={w.a}
							className="flex items-center justify-between rounded-2xl px-4 py-2.5"
							style={{ background: COL.tint }}
						>
							<G size={24}>{w.a}</G>
							<span className="text-right text-sm" style={{ color: COL.mute }}>
								<b style={{ color: COL.ink }}>{w.r}</b>, {w.m}
							</span>
						</div>
					))}
				</div>
			)}
			{audio && (
				<div className="mt-3 flex justify-center">
					<SoundButton asset={audio} />
				</div>
			)}
			<p className="mt-4 text-center text-xs" style={{ color: COL.faint }}>
				{t("letters.taughtIn", { n: u.number, title: u.title })}
			</p>
		</Sheet>
	);
}
